import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Only the default (English) bundle is loaded eagerly so the initial paint
// isn't blocked on ~250KB of locale JSON. Other languages are fetched
// on-demand the first time they are selected.
import en from "./locales/en.json";
import pricingEn from "./locales/pricing-en.json";
import smartActionsEn from "./locales/smart-actions-en.json";

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "ar", label: "العربية", dir: "rtl" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "es", label: "Español", dir: "ltr" },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]["code"];

/** Geo-based language mapping by region */
const GEO_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  SA: "ar", AE: "ar", KW: "ar", QA: "ar", OM: "ar", BH: "ar",
  EG: "ar", JO: "ar", LB: "ar", IQ: "ar", LY: "ar", SD: "ar",
  TN: "ar", DZ: "ar", MA: "ar", YE: "ar", PS: "ar", SY: "ar",
  FR: "fr", BE: "fr", CH: "fr", CA: "fr",
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es",
  VE: "es", EC: "es", GT: "es", CU: "es", BO: "es", DO: "es",
  HN: "es", PY: "es", SV: "es", NI: "es", CR: "es", PA: "es",
  UY: "es", PR: "es",
};

export function getLanguageFromCountry(countryCode: string): SupportedLanguage {
  return GEO_LANGUAGE_MAP[countryCode?.toUpperCase()] || "en";
}

// Track missing keys — log in dev, never silently fallback
const missingKeySet = new Set<string>();

// Track which non-default locales have been loaded
const loadedLocales = new Set<string>(["en"]);

/**
 * Dynamically import & register a locale's resource bundles.
 * Cached — safe to call repeatedly.
 */
async function loadLocale(lng: SupportedLanguage): Promise<void> {
  if (loadedLocales.has(lng)) return;
  loadedLocales.add(lng);

  try {
    if (lng === "ar") {
      const [main, guides, pricing, smartActions] = await Promise.all([
        import("./locales/ar.json"),
        import("./locales/guides-ar.json"),
        import("./locales/pricing-ar.json"),
        import("./locales/smart-actions-ar.json"),
      ]);
      i18n.addResourceBundle("ar", "translation",
        { ...main.default, ...guides.default, ...pricing.default, ...smartActions.default },
        true, true);
    } else if (lng === "fr") {
      const [main, guides, pricing, smartActions] = await Promise.all([
        import("./locales/fr.json"),
        import("./locales/guides-fr.json"),
        import("./locales/pricing-fr.json"),
        import("./locales/smart-actions-fr.json"),
      ]);
      i18n.addResourceBundle("fr", "translation",
        { ...main.default, ...guides.default, ...pricing.default, ...smartActions.default },
        true, true);
    } else if (lng === "es") {
      const [main, guides, pricing, smartActions] = await Promise.all([
        import("./locales/es.json"),
        import("./locales/guides-es.json"),
        import("./locales/pricing-es.json"),
        import("./locales/smart-actions-es.json"),
      ]);
      i18n.addResourceBundle("es", "translation",
        { ...main.default, ...guides.default, ...pricing.default, ...smartActions.default },
        true, true);
    }
  } catch (err) {
    loadedLocales.delete(lng); // allow retry
    console.error(`[i18n] Failed to load locale "${lng}":`, err);
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: { ...en, ...pricingEn, ...smartActionsEn } },
    },
    supportedLngs: ["en", "ar", "fr", "es"],
    fallbackLng: "en",
    // Don't block init waiting for non-default locales — they hydrate async.
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "vazoul_language",
      caches: ["localStorage"],
    },
    saveMissing: true,
    missingKeyHandler: (_lngs, _ns, key, fallbackValue) => {
      if (!missingKeySet.has(key)) {
        missingKeySet.add(key);
        console.warn(`[i18n] Missing translation key: "${key}" (fallback: "${fallbackValue}")`);
      }
    },
  });

/** Apply direction and lang to <html> */
export function applyLanguageDirection(lng: string) {
  const langConfig = SUPPORTED_LANGUAGES.find((l) => l.code === lng);
  const dir = langConfig?.dir || "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", lng);
}

/** Get the user's explicitly selected language (not browser default) */
export function getUserLanguage(): SupportedLanguage {
  const stored = localStorage.getItem("vazoul_language");
  if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) {
    return stored as SupportedLanguage;
  }
  return i18n.language as SupportedLanguage;
}

// Sync language to backend profile
async function syncLanguageToProfile(lng: string) {
  try {
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      await supabase.from("profiles").update({ preferred_language: lng }).eq("user_id", session.user.id);
    }
  } catch {
    // Silent fail — profile sync is best-effort
  }
}

// Listen for language changes — apply direction + ensure bundle is loaded.
i18n.on("languageChanged", (lng) => {
  applyLanguageDirection(lng);
  localStorage.setItem("vazoul_language", lng);
  syncLanguageToProfile(lng);
  if (lng !== "en" && (lng === "ar" || lng === "fr" || lng === "es")) {
    void loadLocale(lng);
  }
});

// If the detected initial language is non-English, kick off its load
// immediately (non-blocking) so the UI re-renders translated copy ASAP.
const initialLng = (i18n.language || "en").split("-")[0] as SupportedLanguage;
if (initialLng !== "en" && SUPPORTED_LANGUAGES.some(l => l.code === initialLng)) {
  void loadLocale(initialLng);
}

// Apply initial direction
applyLanguageDirection(i18n.language);

export default i18n;
